import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getInboxItem,
  downloadAttachment,
  reprocessInboxItem,
  deleteInboxItem,
  restoreInboxItem,
} from '../../services/emailInboxApi';

const CATEGORY_COLORS = {
  order_request: 'bg-primary',
  quote_request: 'bg-success',
  other: 'bg-secondary',
  needs_review: 'bg-warning text-dark',
  blocked: 'bg-danger',
};

const STATUS_COLORS = {
  pending: 'bg-secondary-subtle text-secondary border border-secondary-subtle',
  processing: 'bg-info-subtle text-info border border-info-subtle',
  classified: 'bg-success-subtle text-success border border-success-subtle',
  processed: 'bg-success-subtle text-success border border-success-subtle',
  error: 'bg-danger-subtle text-danger border border-danger-subtle',
  blocked: 'bg-danger-subtle text-danger border border-danger-subtle',
};

const FILE_ICONS = {
  'application/pdf': 'bi-file-earmark-pdf text-danger',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'bi-file-earmark-excel text-success',
  'application/vnd.ms-excel': 'bi-file-earmark-excel text-success',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'bi-file-earmark-word text-primary',
  'application/msword': 'bi-file-earmark-word text-primary',
  'text/csv': 'bi-file-earmark-spreadsheet text-success',
  'text/plain': 'bi-file-earmark-text text-muted',
  'image/png': 'bi-file-earmark-image text-info',
  'image/jpeg': 'bi-file-earmark-image text-info',
  'image/gif': 'bi-file-earmark-image text-info',
  'application/zip': 'bi-file-earmark-zip text-warning',
};

function fileIcon(contentType) {
  return FILE_ICONS[contentType] || 'bi-file-earmark text-muted';
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function HtmlBody({ html }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    const frame = iframeRef.current;
    if (!frame) return;
    const doc = frame.contentDocument || frame.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
    // Auto-size iframe to content
    const resize = () => {
      try {
        frame.style.height = doc.body.scrollHeight + 32 + 'px';
      } catch {}
    };
    resize();
    frame.addEventListener('load', resize);
    return () => frame.removeEventListener('load', resize);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title="Email body"
      sandbox="allow-same-origin"
      style={{ width: '100%', minHeight: 200, border: 'none' }}
    />
  );
}

function AttachmentRow({ att, itemId }) {
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState('');

  async function handleDownload() {
    setDownloading(true);
    setDlError('');
    try {
      await downloadAttachment(itemId, att.id, att.filename);
    } catch (e) {
      setDlError(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="d-flex align-items-center gap-3 py-2 px-3 border-bottom last-child-no-border">
      <i className={`bi ${fileIcon(att.content_type)} fs-5 flex-shrink-0`} />
      <div className="flex-grow-1 min-width-0">
        <div className="text-truncate fw-medium small" title={att.filename}>{att.filename}</div>
        <div className="text-muted" style={{ fontSize: 11 }}>
          {att.content_type}
          {att.size_bytes ? ` · ${formatBytes(att.size_bytes)}` : ''}
        </div>
        {dlError && <div className="text-danger" style={{ fontSize: 11 }}>{dlError}</div>}
        {att.status_error && (
          <div className="text-warning-emphasis" style={{ fontSize: 11 }} title={att.status_error}>
            <i className="bi bi-exclamation-triangle me-1" />{att.status_error}
          </div>
        )}
      </div>
      {att.og_request_id && (
        <a
          href={`/order-x-v4?openRequest=${att.og_request_code || att.og_request_id}`}
          className="badge bg-primary-subtle text-primary border border-primary-subtle text-decoration-none"
          style={{ fontSize: 10 }}
          title={`Open OX Request #${att.og_request_seq ?? att.og_request_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <i className="bi bi-box-arrow-up-right me-1" style={{ fontSize: 9 }} />
          OX-{att.og_request_seq ?? att.og_request_id}
        </a>
      )}
      <button
        type="button"
        className="btn btn-sm btn-outline-primary flex-shrink-0"
        onClick={handleDownload}
        disabled={downloading}
        title={`Download ${att.filename}`}
      >
        {downloading
          ? <span className="spinner-border spinner-border-sm" />
          : <><i className="bi bi-download me-1" />Download</>}
      </button>
    </div>
  );
}

export default function EmailInboxDetail({ itemId, isDeletedView = false, onClose, onDeleted, onRestored }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bodyMode, setBodyMode] = useState('html'); // 'html' | 'text'
  const [reprocessState, setReprocessState] = useState({ loading: false, message: '' });
  const [deleteBusy, setDeleteBusy] = useState(false);

  const loadItem = useCallback(() => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    return getInboxItem(itemId)
      .then((data) => {
        setItem(data);
        setBodyMode((prev) => (data ? (data.body_html ? 'html' : 'text') : prev));
        return data;
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [itemId]);

  useEffect(() => { loadItem(); }, [loadItem]);

  async function handleReprocess() {
    setReprocessState({ loading: true, message: '' });
    try {
      await reprocessInboxItem(itemId);
      setReprocessState({ loading: true, message: 'Reprocessing started…' });
      // Classification + processing runs in the background; poll a few times
      // so the modal reflects the outcome without the user closing/reopening it.
      const poll = async (attempt) => {
        const data = await loadItem();
        if (data?.status === 'pending' && attempt < 6) {
          setTimeout(() => poll(attempt + 1), 3000);
        } else {
          setReprocessState({ loading: false, message: '' });
        }
      };
      setTimeout(() => poll(1), 3000);
    } catch (e) {
      setReprocessState({ loading: false, message: e.message });
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this email? It will be moved to the Deleted bucket.')) return;
    setDeleteBusy(true);
    try {
      await deleteInboxItem(itemId);
      toast.success('Email moved to Deleted');
      onDeleted?.(itemId);
    } catch (e) {
      toast.error(e.message || 'Failed to delete email');
    } finally {
      setDeleteBusy(false);
    }
  }

  async function handleRestore() {
    setDeleteBusy(true);
    try {
      await restoreInboxItem(itemId);
      toast.success('Email restored to Inbox');
      onRestored?.(itemId);
    } catch (e) {
      toast.error(e.message || 'Failed to restore email');
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxWidth: 860 }}>
        <div className="modal-content">

          {/* Header */}
          <div className="modal-header border-bottom py-2 px-4">
            <div className="d-flex align-items-start gap-2 flex-grow-1 min-width-0">
              <i className="bi bi-envelope-open text-primary mt-1 flex-shrink-0" />
              <div className="min-width-0">
                <h6 className="mb-0 text-truncate" style={{ maxWidth: 640 }}>
                  {loading ? 'Loading…' : (item?.subject || '(no subject)')}
                </h6>
                {item && (
                  <div className="d-flex gap-2 mt-1 flex-wrap">
                    <span className={`badge small ${CATEGORY_COLORS[item.category] || 'bg-secondary'}`}>
                      {(item.category || 'unknown').replace(/_/g, ' ')}
                    </span>
                    <span className={`badge small ${STATUS_COLORS[item.status] || 'bg-secondary'}`}>
                      {item.status || '—'}
                    </span>
                    {item.ack_sent_at && (
                      <span className="badge small bg-info-subtle text-info border border-info-subtle">
                        <i className="bi bi-send-check me-1" />Ack sent
                      </span>
                    )}
                    {item.reply_sent_at && (
                      <span className="badge small bg-success-subtle text-success border border-success-subtle">
                        <i className="bi bi-reply me-1" />Reply sent
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <button type="button" className="btn-close ms-3" onClick={onClose} aria-label="Close" />
          </div>

          <div className="modal-body p-0">
            {loading && (
              <div className="text-center py-5 text-muted">
                <span className="spinner-border spinner-border-sm me-2" />Loading email…
              </div>
            )}
            {error && <div className="alert alert-danger m-3">{error}</div>}

            {!loading && item && (
              <>
                {/* Email headers */}
                <div className="px-4 py-3 bg-light border-bottom">
                  <table className="table table-sm table-borderless mb-0 small">
                    <tbody>
                      <tr>
                        <td className="text-muted fw-semibold text-nowrap pe-3" style={{ width: 70 }}>From</td>
                        <td className="text-break">
                          {item.from_name
                            ? <><strong>{item.from_name}</strong> <span className="text-muted">&lt;{item.from_email}&gt;</span></>
                            : item.from_email}
                        </td>
                      </tr>
                      {item.to_email && (
                        <tr>
                          <td className="text-muted fw-semibold text-nowrap pe-3">To</td>
                          <td className="text-break text-muted">{item.to_email}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-muted fw-semibold text-nowrap pe-3">Subject</td>
                        <td><strong>{item.subject || '(no subject)'}</strong></td>
                      </tr>
                      <tr>
                        <td className="text-muted fw-semibold text-nowrap pe-3">Date</td>
                        <td className="text-muted">
                          {item.received_at ? new Date(item.received_at).toLocaleString(undefined, {
                            weekday: 'short', year: 'numeric', month: 'short',
                            day: 'numeric', hour: '2-digit', minute: '2-digit',
                          }) : '—'}
                        </td>
                      </tr>
                      {item.og_request_ids?.length > 0 && (
                        <tr>
                          <td className="text-muted fw-semibold text-nowrap pe-3">
                            OX {item.og_request_ids.length > 1 ? 'Requests' : 'Request'}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {item.og_request_ids.map((id, idx) => (
                                <a
                                  key={id}
                                  href={`/order-x-v4?openRequest=${item.og_request_codes?.[idx] || id}`}
                                  className="badge bg-primary text-white text-decoration-none d-inline-flex align-items-center gap-1"
                                  style={{ fontSize: 11 }}
                                  title={`Open OX Request #${item.og_request_seqs?.[idx] ?? id} in review`}
                                >
                                  <i className="bi bi-box-arrow-up-right" style={{ fontSize: 10 }} />
                                  OX-{item.og_request_seqs?.[idx] ?? id}
                                </a>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Body toggle */}
                {item.body_html && item.body_text && (
                  <div className="px-4 pt-2 d-flex align-items-center gap-2">
                    <span className="small text-muted me-1">View as:</span>
                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className={`btn ${bodyMode === 'html' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setBodyMode('html')}
                      >
                        <i className="bi bi-code-slash me-1" />HTML
                      </button>
                      <button
                        type="button"
                        className={`btn ${bodyMode === 'text' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setBodyMode('text')}
                      >
                        <i className="bi bi-type me-1" />Plain text
                      </button>
                    </div>
                  </div>
                )}

                {/* Body */}
                <div className="px-4 py-3">
                  {bodyMode === 'html' && item.body_html ? (
                    <div className="border rounded overflow-hidden">
                      <HtmlBody html={item.body_html} />
                    </div>
                  ) : item.body_text ? (
                    <pre
                      className="border rounded p-3 small bg-light mb-0"
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 480, overflowY: 'auto' }}
                    >
                      {item.body_text}
                    </pre>
                  ) : (
                    <p className="text-muted small fst-italic mb-0">No email body available.</p>
                  )}
                </div>

                {/* Attachments */}
                {item.attachments?.length > 0 && (
                  <div className="px-4 pb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="bi bi-paperclip text-muted" />
                      <span className="small fw-semibold text-muted">
                        {item.attachments.length} Attachment{item.attachments.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="border rounded">
                      {item.attachments.map((att) => (
                        <AttachmentRow key={att.id} att={att} itemId={item.id} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Classification error */}
                {item.status_error && (
                  <div className="mx-4 mb-3">
                    <div className="alert alert-warning small py-2 mb-0">
                      <i className="bi bi-exclamation-triangle me-2" />
                      <strong>Processing error:</strong> {item.status_error}
                    </div>
                  </div>
                )}

                {/* Reprocess — for anything that failed or needs another look */}
                {(item.category === 'needs_review' || item.status === 'error') && (
                  <div className="mx-4 mb-3 d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleReprocess}
                      disabled={reprocessState.loading}
                    >
                      {reprocessState.loading
                        ? <><span className="spinner-border spinner-border-sm me-1" />Reprocessing…</>
                        : <><i className="bi bi-arrow-repeat me-1" />Reprocess</>}
                    </button>
                    {reprocessState.message && (
                      <span className="small text-muted">{reprocessState.message}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer py-2 px-4 d-flex justify-content-between">
            <div className="small text-muted">
              {item?.classification_attempts > 0 && (
                <span><i className="bi bi-cpu me-1" />AI classification: {item.classification_attempts} attempt{item.classification_attempts !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="d-flex gap-2">
              {item && (isDeletedView ? (
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleRestore} disabled={deleteBusy}>
                  {deleteBusy
                    ? <span className="spinner-border spinner-border-sm me-1" />
                    : <i className="bi bi-arrow-counterclockwise me-1" />}
                  Restore
                </button>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleDelete} disabled={deleteBusy}>
                  {deleteBusy
                    ? <span className="spinner-border spinner-border-sm me-1" />
                    : <i className="bi bi-trash me-1" />}
                  Delete
                </button>
              ))}
              <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
